import {
  FSetupFetchActionType,
  FSetupFetchMethodFunction,
  mergeSchemas,
  Schema,
  SchemaTypesEnum,
} from '@spazious/dollhouse-fsetup';
import * as schemas from '@spazious/dollhouse-schemas';
import { decodeFCompositionId } from '@spazious/dollhouse-shallow-graph';

import catalog from '../catalog.json' assert { type: 'json' };

const { decorations, furnitures } = catalog;

export const defaultFurnitureMapping = furnitures.reduce(
  (acc, furniture) => ({ ...acc, [furniture.id]: furniture }),
  {} as Record<number, (typeof furnitures)[0]>
);

export const defaultDecorationMapping = decorations.reduce(
  (acc, decorations) => ({ ...acc, [decorations.id]: decorations }),
  {} as Record<number, (typeof decorations)[0]>
);

const schemaEntries = Object.entries(schemas);

const findSchemaByNameOrId = (nameOrId: string): Schema => {
  const foundSchema = schemaEntries.find(
    ([, schema]) => schema.name === nameOrId || nameOrId === schema.id
  );

  if (!foundSchema) {
    throw new Error(`Missing schema type: ${nameOrId}`);
  }

  return foundSchema[1] as any;
};

const castArray = <T>(value: T | T[]): NonNullable<T>[] =>
  Array.isArray(value) ? value : value ? [value as any] : [];

const castEnum = <T extends Record<string, any>>(enumObject: T): Record<string, number> =>
  Object.entries(enumObject).reduce(
    (acc, [key, value]) => (Number(value) >= 0 ? { ...acc, [key]: value as number } : acc),
    {} as Record<string, number>
  );

const getSchemas = (schemaTypeName: string): Schema[] => {
  const foundSchema = findSchemaByNameOrId(schemaTypeName);

  return [...castArray(foundSchema.extends).flatMap(getSchemas), foundSchema];
};

export const defaultCompositionSchemaMapping = Object.entries(castEnum(SchemaTypesEnum)).reduce(
  (acc, [schemaTypeName, schemaTypeValue]) => {
    const schemas = getSchemas(schemaTypeName);
    return { ...acc, [schemaTypeValue]: mergeSchemas(...schemas) as Schema };
  },
  {} as Record<number, Schema>
);

export const createFurnitureFetchMethod = (
  mappings: typeof defaultFurnitureMapping = defaultFurnitureMapping
) => {
  return (async (_, id: number) => {
    const furniture = mappings[id as keyof typeof mappings];
    if (!furniture) {
      throw new Error(`Invalid furniture id: ${id}`);
    }
    return furniture;
  }) as FSetupFetchMethodFunction;
};

export const createSchemaFetchMethod = (
  schemaMapping: typeof defaultCompositionSchemaMapping = defaultCompositionSchemaMapping
) => {
  return (async (_, id: number) => {
    const schema = schemaMapping[(id & 0xff) as keyof typeof schemaMapping];
    if (typeof schema !== 'object') {
      throw new Error(`Missing schema id: ${JSON.stringify(decodeFCompositionId(id))}`);
    }

    return { schema };
  }) as FSetupFetchMethodFunction;
};

export const createDecorationFetchMethod = (
  mappings: typeof defaultDecorationMapping = defaultDecorationMapping
) => {
  return (async (_, id: number) => {
    const decoration = mappings[id as keyof typeof mappings];
    if (!decoration) {
      throw new Error(`Invalid furniture id: ${id}`);
    }
    return decoration;
  }) as FSetupFetchMethodFunction;
};

export const defaultFurnitureFetchMethod = createFurnitureFetchMethod();
export const defaultDecorationFetchMethod = createDecorationFetchMethod();
export const defaultSchemaFetchMethod = createSchemaFetchMethod();

export const fetchMethod: FSetupFetchMethodFunction = (type, id) => {
  switch (type) {
    case FSetupFetchActionType.Furniture: {
      return defaultFurnitureFetchMethod(type, id);
    }
    case FSetupFetchActionType.Decoration: {
      return defaultDecorationFetchMethod(type, id);
    }
    default:
      throw new Error(`Invalid fetch method type: ${type}`);
  }
};
